import * as React from 'react'
import { BubbleMenu, BubbleMenuProps, Editor } from '@tiptap/react'
import { cn } from '@/lib/utils'
import { 
  Bold, 
  Italic, 
  Underline, 
  Code, 
  Link,
  Type as TextIcon
} from 'lucide-react' // Assuming you're using lucide-react for icons
import SectionOne from '../section/one'

interface SelectionBubbleMenuProps extends BubbleMenuProps {
  editor: Editor
}

export const SelectionBubbleMenu = ({ editor, children=<></>, ...props }: SelectionBubbleMenuProps) => {
  if (!editor) {
    return null
  }

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ 
        duration: 200,
        placement: 'top',
        arrow: false,
      }}
      shouldShow={({ editor }) => {
        // Only show when there's text selected and editor is focused
        return !editor.isActive('link') && !editor.state.selection.empty && editor.isFocused
      }}
      {...props}
    >
      <div className="flex items-center gap-1 p-1 border rounded-md shadow-md border-border bg-background">
        <SectionOne type='hover-card' editor={editor} activeLevels={[1, 2, 3, 4, 5, 6]}/>
        
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={cn(
            'rounded p-1 hover:bg-muted',
            editor.isActive('bold') ? 'bg-muted text-primary' : 'text-foreground'
          )}
          title="Bold"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={cn(
            'rounded p-1 hover:bg-muted',
            editor.isActive('italic') ? 'bg-muted text-primary' : 'text-foreground'
          )}
          title="Italic"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={cn(
            'rounded p-1 hover:bg-muted',
            editor.isActive('underline') ? 'bg-muted text-primary' : 'text-foreground'
          )}
          title="Underline"
        >
          <Underline className="w-4 h-4" />
        </button>
        <button
          onClick={() => editor.chain().focus().toggleCode().run()}
          className={cn(
            'rounded p-1 hover:bg-muted',
            editor.isActive('code') ? 'bg-muted text-primary' : 'text-foreground'
          )}
          title="Code"
        >
          <Code className="w-4 h-4" />
        </button>
        <button
          onClick={() => {
            // You might want to implement a custom link dialog here
            const url = window.prompt('URL')
            if (url) {
              editor.chain().focus().setLink({ href: url }).run()
            }
          }}
          className={cn(
            'rounded p-1 hover:bg-muted',
            editor.isActive('link') ? 'bg-muted text-primary' : 'text-foreground'
          )}
          title="Link"
        >
          <Link className="w-4 h-4" />
        </button>
        {/* You can add more formatting options as needed */}
      </div>
    </BubbleMenu>
  )
}